import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Crown, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TrialBanner() {
  const { subscriptionStatus, trialDaysLeft, userRole } = useAuth();
  const [loading, setLoading] = useState(false);

  // Don't show for super_admin or if already active/paid
  if (userRole === 'super_admin') return null;
  if (subscriptionStatus !== 'trial' || trialDaysLeft === null) return null;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'create_checkout',
          tenant_id: (await supabase.rpc('get_my_tenant_id')).data,
          plan_key: 'essentiel',
          customer_email: (await supabase.auth.getUser()).data.user?.email,
        },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (err: any) {
      toast.error('Erreur lors de la redirection vers le paiement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const urgent = trialDaysLeft <= 2;

  return (
    <div
      className={`flex shrink-0 items-center justify-between px-4 py-2 text-sm ${
        urgent
          ? 'border-b border-destructive/20 bg-destructive/10 text-destructive'
          : 'border-b border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/30 dark:bg-amber-950/30 dark:text-amber-300'
      }`}
    >
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4" />
        <span className="font-medium">
          Version d'essai —{' '}
          {trialDaysLeft > 0
            ? `${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} restant${trialDaysLeft > 1 ? 's' : ''}`
            : "Expiré aujourd'hui"}
        </span>
      </div>
      <Button
        size="sm"
        variant={urgent ? 'destructive' : 'default'}
        className="h-7 gap-1.5 text-xs"
        onClick={handleUpgrade}
        disabled={loading}
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crown className="h-3 w-3" />}
        Passer en version payante
      </Button>
    </div>
  );
}
